// Utilities to build thinkorswim (TOS) Analyze import lines

const sanitizeOccSymbol = (symbol) => {
  if (!symbol || typeof symbol !== 'string') return '';
  return symbol.replace(/\s+/g, '');
};

const monthNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const extractUnderlyingPart = (occSymbol) => {
  const s = sanitizeOccSymbol(occSymbol);
  const firstDigitIdx = s.search(/[0-9]/);
  if (firstDigitIdx <= 0) return { raw: s, base: s, weekly: false };
  const rawUnderlying = s.substring(0, firstDigitIdx);
  const weekly = rawUnderlying.endsWith('W');
  const base = weekly ? rawUnderlying.substring(0, rawUnderlying.length - 1) : rawUnderlying;
  return { raw: rawUnderlying, base, weekly };
};

const formatTosDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = d.getDate();
  const mon = monthNames[d.getMonth()];
  const yr = String(d.getFullYear()).slice(-2);
  return `${day} ${mon} ${yr}`;
};

const getOptionType = (leg) => {
  const t = (leg?.opt || '').toString().toUpperCase();
  return t === 'CALL' || t === 'C' ? 'CALL' : 'PUT';
};

// priceText: if provided, prepend with '@', otherwise omit
const buildPriceSuffix = (priceText) => {
  if (priceText && String(priceText).trim() !== '') {
    return ` @${priceText} LMT FIXED`;
  }
  return ' LMT FIXED';
};

const toNum = (v) => (typeof v === 'number' ? v : Number(v));
const getLegMark = (leg) => {
  const m = toNum(leg?.mark);
  if (Number.isFinite(m)) return m;
  const bid = toNum(leg?.bid);
  const ask = toNum(leg?.ask);
  if (Number.isFinite(bid) && Number.isFinite(ask)) return (bid + ask) / 2;
  const last = toNum(leg?.last);
  if (Number.isFinite(last)) return last;
  return null;
};

const formatPriceForTos = (value) => {
  if (value === null || value === undefined) return '';
  const rounded = Math.round(Number(value) * 100) / 100;
  let s = rounded.toFixed(2);
  // Prefer .xx instead of 0.xx for readability like TOS examples
  if (s.startsWith('0.')) s = s.substring(1);
  if (s.startsWith('-0.')) s = '-' + s.substring(2);
  return s;
};

export const computeCalendarPrice = (row) => {
  const spread = row?.calendar_spread;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const shortMark = getLegMark(shortLeg);
  const longMark = getLegMark(longLeg);
  if (!Number.isFinite(shortMark) || !Number.isFinite(longMark)) return null;
  return longMark - shortMark;
};

export const computeDiagonalPrice = (row) => {
  const spread = row?.diagonal_spread;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const shortMark = getLegMark(shortLeg);
  const longMark = getLegMark(longLeg);
  if (!Number.isFinite(shortMark) || !Number.isFinite(longMark)) return null;
  return longMark - shortMark;
};

export const computeVerticalDebitPrice = (row) => {
  const spread = row?.vertical_spread;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const shortMark = getLegMark(shortLeg);
  const longMark = getLegMark(longLeg);
  if (!Number.isFinite(shortMark) || !Number.isFinite(longMark)) return null;
  return longMark - shortMark;
};

export const buildTosCalendarLine = (row) => {
  const spread = row?.calendar_spread;
  const qty = typeof row?.calendar_quantity === 'number' && row.calendar_quantity > 0 ? row.calendar_quantity : 1;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const u = extractUnderlyingPart(shortLeg?.symbol || longLeg?.symbol || '');
  const weeklyPart = u.weekly ? ' (Weeklys)' : '';
  const backDate = formatTosDate(longLeg?.expiration);
  const frontDate = formatTosDate(shortLeg?.expiration);
  const strike = longLeg?.strike ?? shortLeg?.strike;
  const type = getOptionType(longLeg || shortLeg);
  const pr = computeCalendarPrice(row);
  const priceSuffix = buildPriceSuffix(formatPriceForTos(pr));
  return `BUY +${qty} CALENDAR ${u.base} 100${weeklyPart} ${backDate}/${frontDate} ${strike} ${type}${priceSuffix}`;
};

export const buildTosDiagonalLine = (row) => {
  const spread = row?.diagonal_spread;
  const qty = typeof row?.diagonal_quantity === 'number' && row.diagonal_quantity > 0 ? row.diagonal_quantity : 1;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const u = extractUnderlyingPart(shortLeg?.symbol || longLeg?.symbol || '');
  const weeklyPart = u.weekly ? ' (Weeklys)' : '';
  const backDate = formatTosDate(longLeg?.expiration);
  const frontDate = formatTosDate(shortLeg?.expiration);
  const type = getOptionType(longLeg || shortLeg);
  const longStrike = longLeg?.strike;
  const shortStrike = shortLeg?.strike;
  const pr = computeDiagonalPrice(row);
  const priceSuffix = buildPriceSuffix(formatPriceForTos(pr));
  return `BUY +${qty} DIAGONAL ${u.base} 100${weeklyPart} ${backDate}/${frontDate} ${longStrike}/${shortStrike} ${type}${priceSuffix}`;
};

export const buildTosVerticalLine = (row) => {
  const spread = row?.vertical_spread;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  // Data convention: [short, long]
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const u = extractUnderlyingPart(shortLeg?.symbol || longLeg?.symbol || '');
  const weeklyPart = u.weekly ? ' (Weeklys)' : '';
  const expiry = formatTosDate(shortLeg?.expiration || longLeg?.expiration);
  const longStrike = longLeg?.strike;
  const shortStrike = shortLeg?.strike;
  const type = getOptionType(longLeg || shortLeg);
  const pr = computeVerticalDebitPrice(row);
  const priceSuffix = buildPriceSuffix(formatPriceForTos(pr));
  return `BUY +1 VERTICAL ${u.base} 100${weeklyPart} ${expiry} ${longStrike}/${shortStrike} ${type}${priceSuffix}`;
};

export const buildTosFileContent = (row) => {
  const lines = [
    buildTosCalendarLine(row),
    buildTosDiagonalLine(row),
    buildTosVerticalLine(row),
  ].filter(Boolean);
  return lines.join('\r\n');
};


